import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Check, MessageSquare, Plus, Trash2, X } from 'lucide-react';
import { Marked } from 'marked';
import { DEFAULT_VERSION_ID, resolveVersion } from '@/versions/registry';
import { MicTranscribeButton } from '@/design-system/MicTranscribeButton';
import { uploadScreenshot } from './api';
import { useComments } from './CommentContext';
import { CommentSidebar } from './CommentSidebar';
import type { CommentResponse, Draft, Reply } from './types';

// Local Marked instance for rendering comment + reply bodies. AI/LLM
// replies come through as Markdown (headings, bold, lists, code, etc.);
// reviewers usually type plain text but might use `**bold**` or `- bullets`.
// Same parser used elsewhere in the app (ChangelogScene, GeneratorScene,
// build-docs.ts) — keeps the bundle on one markdown lib.
//
// gfm: true is the default in marked v18 but stated explicitly so the
// auto-link + table behaviour is intentional. breaks: true converts
// single newlines to <br> so reviewer-typed line breaks render without
// requiring the markdown spec's two-trailing-spaces convention.
const commentMd = new Marked({ gfm: true, breaks: true });

// ---------------------------------------------------------------------------
// Click-area screenshot capture (Layer B)
//
// On comment submit we capture a 480 × 320 PNG centred on the click,
// stamp the pin marker, and upload it to /api/comments/screenshot before
// posting the comment itself. The Worker prepends `![Click area](<url>)`
// to the GitHub issue body so reviewers see the visual context inline.
//
// Hard rule: best-effort. ANY failure (network, oversize, capture error,
// CORS-tainted canvas) is logged to console and the comment is still
// submitted, just without a screenshotUrl. The reviewer must never see
// "couldn't post your comment because screenshot failed."
// ---------------------------------------------------------------------------

// 1080 × 720 viewport pixels — at retina (2× DPR) that's 2160 × 1440 actual
// pixels, ~50% wider on each axis than the original 720×480 crop. Gives
// downstream agents enough surrounding scene context (rail, top-bar edges,
// neighbouring cards) to orient the pin without losing click-area focus.
//
// 3 MiB cap chosen to comfortably accommodate content-dense surfaces at
// native retina DPR without any quality degradation. Rationale (2026-05-18):
// at ~500 comments lifetime — the realistic ceiling for this mock — total
// storage stays under 1.5 GB, well within R2's 10 GB free tier. Screenshots
// are first-class context for reviewers (downstream LLM agents reading
// comment threads orient on the image, not the selector path), so we
// preserve native DPR rather than down-scale on the rare oversize. The
// previous 1.5 MiB cap with retry-at-scale=1 degraded quality on edge cases;
// the new 3 MiB cap accommodates them at full resolution.
//
// If a future surface ever exceeds 3 MiB (unlikely; the densest current
// surface at retina is ~930 KiB), the comment still posts without
// `screenshotUrl` — silent best-effort fallback, unchanged.
const SCREENSHOT_MAX_BYTES = 3 * 1024 * 1024;
const CROP_W = 1080;
const CROP_H = 720;
const PIN_DIAMETER = 22;
const ORE_COLOUR = '#E18A2F';

async function captureClickArea(
  clickX: number,
  clickY: number,
): Promise<Blob | null> {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Hide every comment-overlay affordance before the snapshot so the
  // resulting image is "what the reviewer was looking at" rather than
  // "what the reviewer was looking at + the orange tint + the composer".
  document.body.classList.add('comments-capturing');

  let fullCanvas: HTMLCanvasElement;
  try {
    // Lazy import — keeps modern-screenshot out of the main bundle.
    const { domToCanvas } = await import('modern-screenshot');
    fullCanvas = await domToCanvas(document.body, {
      width: vw,
      height: vh,
      scale: window.devicePixelRatio || 1,
    });
  } finally {
    document.body.classList.remove('comments-capturing');
  }

  // Compute crop rect centred on the click, clamped to viewport edges.
  const cropW = Math.min(CROP_W, vw);
  const cropH = Math.min(CROP_H, vh);
  let cropX = Math.round(clickX - cropW / 2);
  let cropY = Math.round(clickY - cropH / 2);
  cropX = Math.max(0, Math.min(cropX, vw - cropW));
  cropY = Math.max(0, Math.min(cropY, vh - cropH));

  // The full canvas is the viewport at devicePixelRatio (or scale=1 on the
  // retry path). Scale our viewport-pixel crop into canvas-pixel space.
  const sx = fullCanvas.width / vw;
  const sy = fullCanvas.height / vh;

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = Math.round(cropW * sx);
  cropCanvas.height = Math.round(cropH * sy);
  const ctx = cropCanvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(
    fullCanvas,
    Math.round(cropX * sx),
    Math.round(cropY * sy),
    cropCanvas.width,
    cropCanvas.height,
    0,
    0,
    cropCanvas.width,
    cropCanvas.height,
  );

  // Draw the pin marker post-capture (rather than leaving the live DOM pin
  // visible during snapshot) so position is exact and we avoid double-pins
  // from any future overlapping anchor.
  const pinX = (clickX - cropX) * sx;
  const pinY = (clickY - cropY) * sy;
  const pinR = (PIN_DIAMETER / 2) * sx;
  ctx.beginPath();
  ctx.arc(pinX, pinY, pinR, 0, Math.PI * 2);
  ctx.fillStyle = ORE_COLOUR;
  ctx.fill();
  ctx.lineWidth = 2 * sx;
  ctx.strokeStyle = 'rgba(0,0,0,0.18)';
  ctx.stroke();

  return await new Promise<Blob | null>((resolve) => {
    cropCanvas.toBlob((b) => resolve(b), 'image/png');
  });
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

// Build a CSS selector path from a click target up to its anchor element.
// Stops at the anchor so the path stays short and stable.
function buildSelector(target: HTMLElement, anchor: HTMLElement): string {
  const anchorName = anchor.getAttribute('data-comment-anchor') ?? '';
  const head = `[data-comment-anchor="${anchorName}"]`;
  const path: string[] = [];
  let el: HTMLElement | null = target;
  let depth = 0;
  while (el && el !== anchor && depth < 6) {
    let part = el.tagName.toLowerCase();
    if (el.classList.length > 0) {
      part += '.' + Array.from(el.classList).slice(0, 2).join('.');
    }
    const parent = el.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter(
        (c) => c.tagName === el!.tagName,
      );
      if (sameTag.length > 1) {
        const idx = sameTag.indexOf(el) + 1;
        part += `:nth-of-type(${idx})`;
      }
    }
    path.unshift(part);
    el = el.parentElement;
    depth += 1;
  }
  return [head, ...path].join(' > ').slice(0, 500);
}

// Position a popover near a (x, y) anchor with viewport-edge flipping.
type PopoverPos = { left: number; top: number };

function placePopover(
  anchorX: number,
  anchorY: number,
  width: number,
  height: number,
): PopoverPos {
  const margin = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Default: to the right of the anchor.
  let left = anchorX + 16;
  let top = anchorY - 8;

  // Flip horizontally if overflowing right edge.
  if (left + width > vw - margin) {
    left = anchorX - width - 16;
  }
  // Clamp horizontally so we never go off-screen left either.
  if (left < margin) left = margin;

  // Clamp vertically.
  if (top + height > vh - margin) top = vh - height - margin;
  if (top < margin) top = margin;

  return { left, top };
}

// Walk the ancestor scroll-container chain (overflow auto/scroll/hidden) and
// return false if the anchor's rect is fully outside any container — meaning
// the user scrolled the anchor out of its scroll viewport. The pin should
// then disappear rather than float over whatever's now in those screen coords.
function isAnchorVisibleInContainers(anchor: HTMLElement, rect: DOMRect): boolean {
  if (rect.width === 0 && rect.height === 0) return false;
  let el: HTMLElement | null = anchor.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    const style = window.getComputedStyle(el);
    if (
      style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'hidden' ||
      style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'hidden'
    ) {
      const r = el.getBoundingClientRect();
      if (
        rect.bottom <= r.top ||
        rect.top >= r.bottom ||
        rect.right <= r.left ||
        rect.left >= r.right
      ) {
        return false;
      }
    }
    el = el.parentElement;
  }
  return true;
}

// Track an anchor's live screen position via requestAnimationFrame. Triggers a
// re-render only when the rect actually changes, so popovers (ThreadPopover,
// NewCommentPopover) follow scrolling content without spinning state every
// frame. Returns null while the anchor is unmounted or scrolled off-screen.
type AnchorPosition = { pinX: number; pinY: number };

function useAnchorPosition(
  component: string,
  offsetX: number,
  offsetY: number,
): AnchorPosition | null {
  const [pos, setPos] = useState<AnchorPosition | null>(null);
  useEffect(() => {
    let rafId = 0;
    let last = '';
    const tick = () => {
      const anchor = document.querySelector(
        `[data-comment-anchor="${component}"]`,
      ) as HTMLElement | null;
      let next: AnchorPosition | null = null;
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        if (isAnchorVisibleInContainers(anchor, rect)) {
          next = {
            pinX: rect.left + offsetX,
            pinY: rect.top + offsetY,
          };
        }
      }
      // Round to integer pixels so sub-pixel jitter doesn't force re-renders
      // every frame.
      const serial = next ? `${Math.round(next.pinX)},${Math.round(next.pinY)}` : 'null';
      if (serial !== last) {
        last = serial;
        setPos(next);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [component, offsetX, offsetY]);
  return pos;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

// ---------------------------------------------------------------------------
// Overlay root
// ---------------------------------------------------------------------------

export function CommentOverlay() {
  const {
    commentMode,
    comments,
    draft,
    startDraft,
    cancelDraft,
    openThreadNumber,
    closeThread,
    toggleCommentMode,
  } = useComments();

  // Track Alt/Option globally so reviewers can preview + drop a comment
  // modelessly. The click event's own altKey isn't enough — we also want to
  // light up the hover preview while the modifier is held but no click has
  // happened yet. Reset on window blur in case the keyup is missed during
  // an Alt-Tab.
  const [altHeld, setAltHeld] = useState(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') setAltHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') setAltHeld(false);
    };
    const onBlur = () => setAltHeld(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Modeless Alt/Option-click — drop a comment without entering comment mode.
  // Only fires when Alt is held at click time AND the target is a marked
  // surface; everything else passes through to the app's normal handlers.
  // Skipped when commentMode is on because the in-mode capture below already
  // handles clicks (and we'd double-fire otherwise).
  useEffect(() => {
    if (commentMode) return;
    const onAltClick = (e: MouseEvent) => {
      if (!e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('[data-comment-anchor]') as HTMLElement | null;
      if (!anchor) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const rect = anchor.getBoundingClientRect();
      startDraft({
        component: anchor.getAttribute('data-comment-anchor') ?? '',
        selector: buildSelector(target, anchor),
        elementTag: target.tagName,
        elementText: (target.textContent ?? '').slice(0, 200) || undefined,
        elementAriaLabel: target.getAttribute('aria-label'),
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        viewport: [window.innerWidth, window.innerHeight],
        clickX: e.clientX,
        clickY: e.clientY,
      });
    };
    document.addEventListener('click', onAltClick, true);
    return () => document.removeEventListener('click', onAltClick, true);
  }, [commentMode, startDraft]);

  // ---------------------------------------------------------------------
  // Click capture — only active in comment mode.
  //
  // While comment mode is on, pointer interactions on commentable surfaces
  // are swallowed and turned into new-comment drafts. Two passthrough
  // classes preserve normal click behaviour even with comment mode on:
  //   - data-comment-ui="true"  — our own popovers, toggles, pins
  //   - data-comment-nav="true" — app navigation affordances (logo,
  //                               module rail buttons, breadcrumb, etc.)
  // The nav passthrough is what lets reviewers move between pages without
  // toggling comment mode off.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!commentMode) return;

    const isPassthrough = (e: Event): boolean => {
      const path = (e.composedPath?.() ?? []) as EventTarget[];
      for (const node of path) {
        if (
          node instanceof HTMLElement &&
          (node.dataset.commentUi === 'true' || node.dataset.commentNav === 'true')
        ) {
          return true;
        }
      }
      return false;
    };

    // Swallow the gesture so React's delegated onClick / mousedown handlers
    // never fire. preventDefault alone isn't enough — useNavigate() etc. live
    // in onClick handlers that don't depend on the default action.
    const swallow = (e: Event) => {
      if (isPassthrough(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    const onClick = (e: MouseEvent) => {
      if (isPassthrough(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('[data-comment-anchor]') as HTMLElement | null;
      if (!anchor) return; // unmarked surface — no-op (TODO: toast)

      const rect = anchor.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const draft: Draft = {
        component: anchor.getAttribute('data-comment-anchor') ?? '',
        selector: buildSelector(target, anchor),
        elementTag: target.tagName,
        elementText: (target.textContent ?? '').slice(0, 200) || undefined,
        elementAriaLabel: target.getAttribute('aria-label'),
        offsetX,
        offsetY,
        viewport: [window.innerWidth, window.innerHeight],
        clickX: e.clientX,
        clickY: e.clientY,
      };
      startDraft(draft);
    };

    document.addEventListener('mousedown', swallow, true);
    document.addEventListener('mouseup', swallow, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('mousedown', swallow, true);
      document.removeEventListener('mouseup', swallow, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [commentMode, startDraft]);

  // ---------------------------------------------------------------------
  // Hover tracking — the cursor follows a (+ Comment on <Name>) hint while
  // in comment mode, and the currently-hovered anchor gets a soft ore
  // outline. Both are pointer-events: none so they never block clicks.
  // ---------------------------------------------------------------------
  type HoverInfo =
    | { kind: 'anchor'; name: string; rect: DOMRect }
    | { kind: 'nav' }
    | { kind: 'ui' }
    | { kind: 'none' };

  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState<HoverInfo>({ kind: 'none' });

  useEffect(() => {
    if (!commentMode && !altHeld) {
      setCursor(null);
      setHover({ kind: 'none' });
      return;
    }
    let raf = 0;
    const handler = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setCursor({ x: e.clientX, y: e.clientY });
        const target = e.target as HTMLElement | null;
        if (!target) {
          setHover({ kind: 'none' });
          return;
        }
        if (target.closest('[data-comment-ui="true"]')) {
          setHover({ kind: 'ui' });
          return;
        }
        if (target.closest('[data-comment-nav="true"]')) {
          setHover({ kind: 'nav' });
          return;
        }
        const anchor = target.closest('[data-comment-anchor]') as HTMLElement | null;
        if (anchor) {
          setHover({
            kind: 'anchor',
            name: anchor.getAttribute('data-comment-anchor') ?? '',
            rect: anchor.getBoundingClientRect(),
          });
          return;
        }
        setHover({ kind: 'none' });
      });
    };
    document.addEventListener('mousemove', handler);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', handler);
    };
  }, [commentMode, altHeld]);

  // ---------------------------------------------------------------------
  // Body-level cursor: crosshair when commentMode is on OR Alt is held, so
  // the modeless gesture also signals "I'm about to drop a pin". CSS only.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!commentMode && !altHeld) {
      document.body.style.removeProperty('cursor');
      return;
    }
    document.body.style.cursor = 'crosshair';
    return () => {
      document.body.style.removeProperty('cursor');
    };
  }, [commentMode, altHeld]);

  // ---------------------------------------------------------------------
  // Scroll-to-anchor when a thread is opened (e.g. from the sidebar).
  // Retries a few times because the anchor may not be mounted yet right
  // after a route change.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (openThreadNumber === null) return;
    const target = comments.find((c) => c.number === openThreadNumber);
    if (!target) return;

    let attempts = 0;
    let timerId: number | null = null;

    const tryScroll = () => {
      const anchor = document.querySelector(
        `[data-comment-anchor="${target.component}"]`,
      ) as HTMLElement | null;
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (attempts++ < 8) {
        timerId = window.setTimeout(tryScroll, 60);
      }
    };
    tryScroll();

    return () => {
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [openThreadNumber, comments]);

  // ---------------------------------------------------------------------
  // Keyboard:
  //   Esc  — close topmost popover, then exit comment mode.
  //   c    — toggle comment mode (Figma-style). Plain key, ignored while
  //          typing so reviewers can write 'c' inside the composer textarea
  //          or any other input without flipping mode.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (draft) {
          cancelDraft();
          return;
        }
        if (openThreadNumber !== null) {
          closeThread();
          return;
        }
        if (commentMode) toggleCommentMode();
        return;
      }
      if (
        (e.key === 'c' || e.key === 'C') &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        !isTyping()
      ) {
        e.preventDefault();
        toggleCommentMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commentMode, draft, openThreadNumber, cancelDraft, closeThread, toggleCommentMode]);

  // Pin positioning is now done by PinLayer via direct DOM updates on each
  // scroll event (no React re-render), so pins never lag behind their
  // anchors during scrolling.

  const activeComment = useMemo(
    () =>
      openThreadNumber !== null
        ? comments.find((c) => c.number === openThreadNumber) ?? null
        : null,
    [openThreadNumber, comments],
  );

  return (
    <>
      {/* Comment-mode tint: a 2px inset border around the viewport */}
      {commentMode && (
        <div
          data-comment-ui="true"
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            border: '2px solid var(--accent)',
            opacity: 0.35,
            zIndex: 9990,
          }}
        />
      )}

      {/* Snap highlight — soft ore outline on the currently-hovered anchor.
          Shows in comment mode OR while Alt is held (the modeless gesture). */}
      {(commentMode || altHeld) && hover.kind === 'anchor' && !draft && openThreadNumber === null && (
        <div
          data-comment-ui="true"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: hover.rect.left - 2,
            top: hover.rect.top - 2,
            width: hover.rect.width + 4,
            height: hover.rect.height + 4,
            border: '2px solid var(--accent)',
            background: 'rgba(225, 138, 47, 0.06)',
            borderRadius: 4,
            pointerEvents: 'none',
            transition: 'all 70ms ease',
            zIndex: 9992,
          }}
        />
      )}

      {/* Cursor hint — follows the mouse with a + Comment / ↗ Navigate label */}
      {(commentMode || altHeld) && cursor && !draft && openThreadNumber === null && (
        <div
          data-comment-ui="true"
          aria-hidden="true"
          className="glass-deep"
          style={{
            position: 'fixed',
            left: Math.min(cursor.x + 14, window.innerWidth - 200),
            top: Math.min(cursor.y + 14, window.innerHeight - 40),
            padding: '4px 8px 4px 6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--ink-primary)',
            pointerEvents: 'none',
            zIndex: 9999,
            whiteSpace: 'nowrap',
            borderRadius: 6,
          }}
        >
          {hover.kind === 'anchor' ? (
            <>
              <Plus size={12} style={{ color: 'var(--accent)' }} aria-hidden="true" />
              <span>Comment on {hover.name.split('.').join(' › ')}</span>
            </>
          ) : hover.kind === 'nav' ? (
            <>
              <ArrowUpRight size={12} style={{ color: 'var(--ink-secondary)' }} aria-hidden="true" />
              <span style={{ color: 'var(--ink-secondary)' }}>Click to navigate</span>
            </>
          ) : hover.kind === 'ui' ? null : (
            <>
              <Plus size={12} style={{ color: 'var(--ink-tertiary)' }} aria-hidden="true" />
              <span style={{ color: 'var(--ink-tertiary)' }}>Hover a marked surface</span>
            </>
          )}
        </div>
      )}

      {/* Pin layer — only when comment mode is on; toggle off hides them. */}
      {commentMode && (
        <PinLayer
          comments={comments}
          commentMode={commentMode}
          openThreadNumber={openThreadNumber}
        />
      )}

      {/* New-comment popover */}
      {draft && (
        <NewCommentPopover draft={draft} onClose={cancelDraft} />
      )}

      {/* Thread popover */}
      {activeComment && (
        <ThreadPopover comment={activeComment} onClose={closeThread} />
      )}

      {/* Comment list sidebar — independent of comment mode */}
      <CommentSidebar />
    </>
  );
}

// ---------------------------------------------------------------------------
// Pin icon
// ---------------------------------------------------------------------------

// One SVG that draws the background circle AND the speech-bubble icon together.
// Replaces an earlier two-element setup (button background + nested
// MessageSquare from lucide) which needed a hand-tuned CSS `transform` on
// the icon to compensate for the bubble's visual centre being offset from
// the SVG bbox centre. Drawing both shapes in one SVG eliminates the
// composition problem — the icon's position relative to the circle is frozen
// at draw time, not assembled at runtime by flex + transform.
//
// The bubble path is copied verbatim from lucide-react v0.379's
// `MessageSquare` — the same glyph rendered by the comment-mode toggle in
// the top bar (CommentToggles.tsx). Keeping the path in sync visually
// anchors the pin to the toggle: clicking the toggle and dropping a pin
// produce icons that match. If lucide-react is bumped and the
// `MessageSquare` path changes, this constant should be re-synced.
//
// Native viewBox 24×24; the path extends x:3–21, y:3–21. Widened here to
// `-12 -12 48 48` so the icon occupies the centre ~50% of the rendered pin,
// leaving ~12 units of breathing room between the bubble outline and the
// circle edge. Padding knob: shrink the viewBox extent → less padding;
// grow it → more.
const MESSAGE_SQUARE_PATH =
  'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z';

function CommentPinIcon({ resolved }: { resolved: boolean }) {
  const fill = resolved ? 'var(--ink-tertiary)' : 'var(--accent)';
  return (
    <svg
      width={22}
      height={22}
      viewBox="-12 -12 48 48"
      fill="none"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <circle cx={12} cy={12} r={24} fill={fill} />
      <path
        d={MESSAGE_SQUARE_PATH}
        stroke="#fff"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pin layer
// ---------------------------------------------------------------------------

function PinLayer({
  comments,
  commentMode,
  openThreadNumber,
}: {
  comments: CommentResponse[];
  commentMode: boolean;
  openThreadNumber: number | null;
}) {
  const { openThread } = useComments();
  const layerRef = useRef<HTMLDivElement | null>(null);

  // Drive pin positions from a requestAnimationFrame loop instead of scroll
  // events. Scroll events don't bubble, and the capture-phase listener on
  // window can miss inner scroll containers in some browsers / composited-
  // scroll cases — letting pins freeze at last-computed coords while the
  // page scrolled underneath. rAF reads getBoundingClientRect() per pin
  // every frame, which is cheap and survives every scroll-chain config,
  // drawer animation, and content reflow.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    let rafId = 0;

    const updatePositions = () => {
      const pins = layer.querySelectorAll<HTMLButtonElement>('[data-pin-number]');
      pins.forEach((pinEl) => {
        const numberStr = pinEl.dataset.pinNumber;
        if (!numberStr) return;
        const number = Number.parseInt(numberStr, 10);
        const c = comments.find((cm) => cm.number === number);
        if (!c) return;
        const anchor = document.querySelector(
          `[data-comment-anchor="${c.component}"]`,
        ) as HTMLElement | null;
        if (!anchor) {
          pinEl.style.display = 'none';
          return;
        }
        const rect = anchor.getBoundingClientRect();
        // Hide pins on anchors that are clipped off (closed drawer, or
        // scrolled outside their scroll container).
        if (!isAnchorVisibleInContainers(anchor, rect)) {
          pinEl.style.display = 'none';
          return;
        }
        pinEl.style.display = '';
        pinEl.style.left = `${rect.left + c.offsetX - 11}px`;
        pinEl.style.top = `${rect.top + c.offsetY - 11}px`;
      });
      rafId = requestAnimationFrame(updatePositions);
    };

    rafId = requestAnimationFrame(updatePositions);
    return () => cancelAnimationFrame(rafId);
  }, [comments]);

  return (
    <div ref={layerRef}>
      {comments.map((c) => {
        const isOpen = openThreadNumber === c.number;
        const isResolved = c.state === 'closed';
        return (
          <button
            key={c.number}
            data-comment-ui="true"
            data-pin-number={c.number}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openThread(c.number);
            }}
            title={`${c.author}: ${c.body.split('\n')[0].slice(0, 80)}`}
            style={{
              position: 'fixed',
              // Initial position offscreen so we never flash a pin at (0, 0);
              // the useEffect below overwrites left/top from the anchor rect.
              left: -9999,
              top: -9999,
              width: 22,
              height: 22,
              padding: 0,
              border: 'none',
              cursor: 'pointer',
              background: 'transparent',
              opacity: isResolved ? 0.5 : 1,
              boxSizing: 'border-box',
              // Pin shadow + ring + scale are still owned by the button so
              // they animate as a single unit on hover/open.
              borderRadius: '50%',
              boxShadow: isOpen
                ? '0 0 0 3px rgba(225,138,47,0.30), 0 2px 6px rgba(0,0,0,0.20)'
                : '0 1px 4px rgba(0,0,0,0.18)',
              transform: isOpen ? 'scale(1.08)' : 'scale(1)',
              zIndex: commentMode ? 9995 : 9991,
              transition: 'box-shadow 120ms ease, transform 120ms ease',
            }}
            aria-label={`Comment #${c.number} by ${c.author}`}
          >
            <CommentPinIcon resolved={isResolved} />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New-comment popover (with inline username prompt if first time)
// ---------------------------------------------------------------------------

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT_ESTIMATE = 220;

function NewCommentPopover({ draft, onClose }: { draft: Draft; onClose: () => void }) {
  const { username, setUsername, submitDraft, version } = useComments();
  const [params, setParams] = useSearchParams();
  const [nameInput, setNameInput] = useState(username ?? '');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const onOldVersion = version !== DEFAULT_VERSION_ID;
  const latestLabel = resolveVersion(DEFAULT_VERSION_ID).label.split(' — ')[0];

  function jumpToLatest() {
    const next = new URLSearchParams(params);
    next.set('v', DEFAULT_VERSION_ID);
    setParams(next, { replace: false });
    onClose();
  }

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Track the draft's anchor so the composer follows the page when the
  // reviewer scrolls mid-draft. Falls back to the original click coords
  // if the anchor is unmounted (rare — only if the underlying surface
  // re-renders away during drafting).
  const anchorPos = useAnchorPosition(draft.component, draft.offsetX, draft.offsetY);
  const pinX = anchorPos ? anchorPos.pinX : draft.clickX;
  const pinY = anchorPos ? anchorPos.pinY : draft.clickY;
  const pos = placePopover(pinX, pinY, POPOVER_WIDTH, POPOVER_HEIGHT_ESTIMATE);

  const canPost = body.trim().length > 0 && (username || nameInput.trim().length > 0);

  async function onPost() {
    if (!canPost) return;
    if (!username && nameInput.trim()) setUsername(nameInput);
    setSubmitting(true);
    setError(null);

    // Best-effort click-area screenshot. Any throw, oversize blob, or
    // capture error degrades silently — the comment still submits without
    // `screenshotUrl`. Reviewers never see a failed-screenshot message.
    let screenshotUrl: string | undefined;
    try {
      const blob = await captureClickArea(draft.clickX, draft.clickY);
      if (blob && blob.size <= SCREENSHOT_MAX_BYTES) {
        screenshotUrl = await uploadScreenshot(blob);
      } else if (blob && blob.size > SCREENSHOT_MAX_BYTES) {
        // eslint-disable-next-line no-console
        console.warn(
          `[comments] screenshot ${blob.size} bytes > ${SCREENSHOT_MAX_BYTES} cap, skipping`,
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[comments] screenshot capture/upload failed; submitting without image', err);
    }

    try {
      await submitDraft(body.trim(), screenshotUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-comment-ui="true"
      className="glass-deep"
      style={popoverShellStyle(pos.left, pos.top, POPOVER_WIDTH)}
      role="dialog"
      aria-label="New comment"
    >
      <PopoverHeader
        title={`Comment on ${draft.component}`}
        onClose={onClose}
      />

      <div style={{ padding: '10px 12px' }}>
        {!username && (
          <>
            <label style={fieldLabelStyle}>👋 Who are you?</label>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Your name"
              maxLength={60}
              style={inputStyle}
            />
            <div style={{ height: 10 }} />
          </>
        )}

        <label style={fieldLabelStyle}>Your comment</label>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What did you notice?"
          rows={4}
          maxLength={2000}
          style={textareaStyle}
        />

        {error && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--state-bad, #b14545)' }}>
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 6,
            marginTop: 10,
          }}
        >
          <MicTranscribeButton value={body} onChange={setBody} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onClose} style={buttonGhostStyle} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              onClick={onPost}
              disabled={!canPost || submitting}
              style={buttonPrimaryStyle(!canPost || submitting)}
            >
              {submitting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      </div>

      {onOldVersion && (
        <button
          type="button"
          onClick={jumpToLatest}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            padding: '8px 12px',
            background: 'rgba(177, 69, 69, 0.08)',
            borderTop: '1px solid rgba(177, 69, 69, 0.35)',
            border: 'none',
            borderBottomLeftRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
            color: 'var(--state-bad, #b14545)',
            fontSize: 11,
            fontWeight: 500,
            textAlign: 'left',
            cursor: 'pointer',
            lineHeight: 1.4,
          }}
          title={`Jump to ${latestLabel}`}
        >
          <AlertTriangle size={12} aria-hidden="true" />
          <span>
            You're not on the latest version. Click to jump to {latestLabel}.
          </span>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread popover
// ---------------------------------------------------------------------------

const THREAD_WIDTH = 380;
const THREAD_HEIGHT_ESTIMATE = 320;

function ThreadPopover({
  comment,
  onClose,
}: {
  comment: CommentResponse;
  onClose: () => void;
}) {
  const {
    username,
    setUsername,
    fetchReplies,
    postReply,
    resolve,
    reopen,
    deleteComment,
  } = useComments();
  const [replies, setReplies] = useState<Reply[] | null>(null);
  const [replyText, setReplyText] = useState('');
  const [nameInput, setNameInput] = useState(username ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const canDelete = !!username && username === comment.author;

  useEffect(() => {
    let cancelled = false;
    fetchReplies(comment.number).then((rs) => {
      if (!cancelled) setReplies(rs);
    });
    return () => {
      cancelled = true;
    };
  }, [comment.number, fetchReplies]);

  // Live anchor position — thread popover follows the pin as the user
  // scrolls or when the layout reflows. Falls back to viewport center if
  // the anchor is unmounted or scrolled outside its container.
  const anchorPos = useAnchorPosition(comment.component, comment.offsetX, comment.offsetY);
  const pinX = anchorPos ? anchorPos.pinX : window.innerWidth / 2;
  const pinY = anchorPos ? anchorPos.pinY : window.innerHeight / 2;
  const pos = placePopover(pinX, pinY, THREAD_WIDTH, THREAD_HEIGHT_ESTIMATE);

  const canReply = replyText.trim().length > 0 && (username || nameInput.trim().length > 0);
  const isResolved = comment.state === 'closed';

  async function onPostReply() {
    if (!canReply) return;
    if (!username && nameInput.trim()) setUsername(nameInput);
    setSubmitting(true);
    setError(null);
    try {
      await postReply(comment.number, replyText.trim());
      setReplyText('');
      const fresh = await fetchReplies(comment.number);
      setReplies(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResolve() {
    setSubmitting(true);
    try {
      await resolve(comment.number);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  async function onReopen() {
    setSubmitting(true);
    try {
      await reopen(comment.number);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await deleteComment(comment.number);
      // deleteComment calls setOpenThreadNumber(null) so the popover closes.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div
      data-comment-ui="true"
      className="glass-deep"
      style={popoverShellStyle(pos.left, pos.top, THREAD_WIDTH)}
      role="dialog"
      aria-label={`Thread #${comment.number}`}
    >
      <PopoverHeader
        title={`${comment.component.split('.').join(' › ')} · ${comment.route}`}
        onClose={onClose}
        actions={
          canDelete &&
          (confirmingDelete ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10.5,
                color: 'var(--state-bad, #b14545)',
                marginRight: 4,
              }}
            >
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={submitting}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--ink-tertiary)',
                  fontSize: 11,
                  padding: '2px 6px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={submitting}
                style={{
                  background: 'var(--state-bad, #b14545)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: submitting ? 'wait' : 'pointer',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '3px 8px',
                }}
              >
                {submitting ? 'Deleting…' : 'Delete'}
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              aria-label="Delete comment"
              title="Delete this comment"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: 'var(--ink-tertiary)',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          ))
        }
      />

      <div style={{ padding: '10px 12px', maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Root comment */}
        <ThreadEntry author={comment.author} body={comment.body} createdAt={comment.createdAt} root />

        {/* Replies */}
        {replies === null && (
          <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 8 }}>
            Loading replies…
          </div>
        )}
        {replies?.map((r) => (
          <ThreadEntry
            key={r.id}
            author={r.author}
            body={r.body}
            createdAt={r.createdAt}
          />
        ))}

        {/* Reply composer (only if thread is open) */}
        {!isResolved && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hairline)' }}>
            {!username && (
              <>
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Your name"
                  maxLength={60}
                  style={{ ...inputStyle, marginBottom: 6 }}
                />
              </>
            )}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Reply…"
              rows={2}
              maxLength={2000}
              style={textareaStyle}
            />
            {error && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--state-bad, #b14545)' }}>
                {error}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 8,
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  onClick={onResolve}
                  disabled={submitting}
                  style={buttonGhostStyle}
                  title="Mark this comment as resolved"
                >
                  <Check size={12} style={{ marginRight: 4 }} aria-hidden="true" />
                  Resolve
                </button>
                <MicTranscribeButton value={replyText} onChange={setReplyText} />
              </div>
              <button
                type="button"
                onClick={onPostReply}
                disabled={!canReply || submitting}
                style={buttonPrimaryStyle(!canReply || submitting)}
              >
                {submitting ? 'Posting…' : 'Post reply'}
              </button>
            </div>
          </div>
        )}

        {/* Resolved state */}
        {isResolved && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid var(--hairline)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 11,
              color: 'var(--ink-tertiary)',
            }}
          >
            <span>This comment has been resolved.</span>
            <button
              type="button"
              onClick={onReopen}
              disabled={submitting}
              style={buttonGhostStyle}
            >
              Reopen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadEntry({
  author,
  body,
  createdAt,
  root,
}: {
  author: string;
  body: string;
  createdAt: string;
  root?: boolean;
}) {
  // The Worker stores root bodies starting with "**From**: <name> · <date>\n\n<body>"
  // — strip that prefix when rendering.
  const cleanBody = body.replace(/^\*\*From\*\*:[^\n]*\n\n?/, '').trim();
  // Render through marked so AI/LLM-formatted replies (markdown headings,
  // bold, lists, code blocks) read correctly instead of as literal ** and
  // ## characters. Reviewer-typed plain text rendering is unchanged
  // (markdown is idempotent on text with no special syntax). Trust model:
  // comment bodies originate from authenticated reviewers or the LLM —
  // same trust posture as the existing marked consumers (build-doc drawer,
  // changelog scene). dangerouslySetInnerHTML matches that pattern.
  const html = commentMd.parse(cleanBody, { async: false }) as string;
  return (
    <div style={{ paddingTop: root ? 0 : 10, paddingBottom: 4 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          fontSize: 11.5,
        }}
      >
        <span style={{ color: 'var(--ink-primary)', fontWeight: 500 }}>{author}</span>
        <span style={{ color: 'var(--ink-tertiary)' }}>{timeAgo(createdAt)}</span>
      </div>
      <div
        className="comment-md"
        style={{ marginTop: 4 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared popover header + styles
// ---------------------------------------------------------------------------

function PopoverHeader({
  title,
  onClose,
  actions,
}: {
  title: string;
  onClose: () => void;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 10px 8px 12px',
        borderBottom: '1px solid var(--hairline)',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--ink-primary)',
          fontWeight: 500,
          minWidth: 0,
        }}
      >
        <MessageSquare size={12} style={{ color: 'var(--accent)' }} aria-hidden="true" />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {actions}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            color: 'var(--ink-tertiary)',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function popoverShellStyle(left: number, top: number, width: number): CSSProperties {
  return {
    position: 'fixed',
    left,
    top,
    width,
    zIndex: 9998,
    padding: 0,
    overflow: 'hidden',
  };
}

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
  color: 'var(--ink-tertiary)',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--hairline-strong)',
  borderRadius: 4,
  background: 'var(--bg-elevated, var(--bg-base))',
  color: 'var(--ink-primary)',
  fontSize: 12.5,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 60,
  lineHeight: 1.5,
};

const buttonGhostStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--hairline-strong)',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 11.5,
  color: 'var(--ink-secondary)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

function buttonPrimaryStyle(disabled: boolean): CSSProperties {
  return {
    background: disabled ? 'var(--hairline-strong)' : 'var(--accent)',
    border: 'none',
    borderRadius: 4,
    padding: '4px 12px',
    fontSize: 11.5,
    color: disabled ? 'var(--ink-tertiary)' : '#fff',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
